import { containersAPI } from '../../services/api';
import type {
  CategoryPhotosGroup,
  ContainerPhotoItem,
  ExtractedImage,
} from '../types/containerPhoto';
import {
  CONTAINER_PHOTO_CATEGORY_ORDER,
  getCategoryLabel,
} from '../types/containerPhoto';

async function blobToExtractedImage(blob: Blob, name: string): Promise<ExtractedImage> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return {
    base64: btoa(binary),
    contentType: blob.type || 'image/jpeg',
    name,
  };
}

function sortGroups(groups: CategoryPhotosGroup[]): CategoryPhotosGroup[] {
  const orderIndex = new Map(CONTAINER_PHOTO_CATEGORY_ORDER.map((cat, idx) => [cat, idx]));
  return [...groups].sort((a, b) => {
    const ai = orderIndex.get(a.category) ?? 999;
    const bi = orderIndex.get(b.category) ?? 999;
    if (ai !== bi) return ai - bi;
    return a.label.localeCompare(b.label);
  });
}

export async function loadContainerCategoryPhotos(
  containerId: number,
  options: {
    photos?: ContainerPhotoItem[];
    sealPhoto?: string | null;
    sealPhotoName?: string | null;
  }
): Promise<CategoryPhotosGroup[]> {
  const { photos, sealPhoto, sealPhotoName } = options;

  if (photos && photos.length > 0) {
    const byCategory = new Map<string, ExtractedImage[]>();

    await Promise.all(
      photos.map(async (photo) => {
        try {
          const ext = await containersAPI.getCategoryPhotoExtracted(containerId, photo.id);
          const images =
            ext.data?.status === 'success' && ext.data.data?.images
              ? ext.data.data.images
              : [];
          if (images.length === 0) return;
          const existing = byCategory.get(photo.category) ?? [];
          byCategory.set(photo.category, [...existing, ...images]);
        } catch {
          /* skip failed photo */
        }
      })
    );

    const groups: CategoryPhotosGroup[] = Array.from(byCategory.entries()).map(([category, images]) => ({
      category,
      label: getCategoryLabel(category),
      images,
    }));

    return sortGroups(groups);
  }

  const groups: CategoryPhotosGroup[] = [];

  if (sealPhoto) {
    try {
      const response = await containersAPI.getSealPhoto(containerId);
      const blob = response.data as Blob;
      if (blob && blob.size > 0) {
        const image = await blobToExtractedImage(blob, sealPhotoName ?? 'seal.jpg');
        groups.push({
          category: 'seal',
          label: getCategoryLabel('seal'),
          images: [image],
        });
      }
    } catch {
      /* no seal photo */
    }
  }

  try {
    const ext = await containersAPI.getContainerPhotosExtracted(containerId);
    const images =
      ext.data?.status === 'success' && ext.data.data?.images ? ext.data.data.images : [];
    if (images.length > 0) {
      groups.push({
        category: 'container',
        label: getCategoryLabel('container'),
        images,
      });
    }
  } catch {
    /* no container photos */
  }

  return sortGroups(groups);
}
